import React, { useEffect, useState } from "react";
import { db, ref } from "./firebase";
import { onValue, update, push, remove } from "firebase/database";
import { formatCourseId } from "./utils/formatCourse";

const ChaptersManagment = () => {
  const [courses, setCourses] = useState([]);
  const [activeCourse, setActiveCourse] = useState("");
  const [chapters, setChapters] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [newChapterName, setNewChapterName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  // Fetch courses
  useEffect(() => {
    const coursesRef = ref(db, "courses");
    const unsub = onValue(coursesRef, (snapshot) => {
      if (snapshot.exists()) {
        setCourses(Object.keys(snapshot.val()));
        // Only set activeCourse if it is not set yet
        setActiveCourse(prev => prev || Object.keys(snapshot.val())[0] || "");
      }
    });
    return () => unsub();
  }, []);

  // Fetch chapters for active course
  useEffect(() => {
    if (!activeCourse) return;
    const chaptersRef = ref(db, `courses/${activeCourse}/chapters`);
    const unsub = onValue(chaptersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const arr = Object.entries(data).map(([id, ch]) => ({
          id,
          name: ch.name || "",
          done: !!ch.done,
          completedAt: ch.completedAt || null
        }));
        setChapters(arr);
      } else {
        setChapters([]);
      }
    });
    return () => unsub();
  }, [activeCourse]);

  // Add chapter
  const handleAddChapter = async () => {
    if (!newChapterName.trim() || !activeCourse) return;
    await push(ref(db, `courses/${activeCourse}/chapters`), {
      name: newChapterName.trim(),
      done: false,
      completedAt: null
    });
    setNewChapterName("");
  };

  // Edit chapter
  const startEdit = (id, name) => {
    setEditingId(id);
    setEditingName(name);
  };
  const saveEdit = async () => {
    if (!editingName.trim() || !editingId) return;
    await update(ref(db, `courses/${activeCourse}/chapters/${editingId}`), {
      name: editingName.trim()
    });
    setEditingId(null);
    setEditingName("");
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  // Toggle done
  const toggleDone = async (id, done) => {
    await update(ref(db, `courses/${activeCourse}/chapters/${id}`), {
      done: !done,
      completedAt: !done ? new Date().toISOString() : null
    });
  };

  // Delete chapter
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this chapter?")) {
      await remove(ref(db, `courses/${activeCourse}/chapters/${id}`));
    }
  };

  // Filtered chapters
  const filteredChapters = chapters.filter(ch =>
    ch.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-3 sm:p-4 md:p-6 text-gray-900 min-h-screen">
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-gray-900">Chapters</h1>

      {/* Course Tabs */}
      <div className="flex flex-wrap gap-2 mb-4 sm:mb-6 p-2 overflow-x-auto">
        {courses.map(course => (
          <button
            key={course}
            onClick={() => setActiveCourse(course)}
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-md transition-colors text-sm sm:text-base whitespace-nowrap ${activeCourse === course
              ? "bg-teal-600 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
          >
            {formatCourseId(course)}
          </button>
        ))}
      </div>

      {/* Search and Add Chapter */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <input
          type="text"
          placeholder="Search chapters..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="p-2 sm:p-2.5 border rounded-md text-gray-900 text-sm sm:text-base"
        />
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            placeholder="New chapter name"
            value={newChapterName}
            onChange={e => setNewChapterName(e.target.value)}
            className="w-full p-2 sm:p-2.5 border rounded-md text-gray-900 text-sm sm:text-base"
          />
          <button
            onClick={handleAddChapter}
            className="bg-green-600 text-white p-2 sm:p-2.5 rounded-md hover:bg-green-700 whitespace-nowrap text-sm sm:text-base"
          >
            Add Chapter
          </button>
        </div>
      </div>

      {/* Chapters Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Desktop Header */}
        <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-gray-50 font-semibold text-gray-900">
          <div className="col-span-6">Chapter Name</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-4">Actions</div>
        </div>
        {filteredChapters.length === 0 ? (
          <div className="p-6 text-center text-gray-600 text-sm sm:text-base">
            No chapters found. Add your first chapter above.
          </div>
        ) : (
          filteredChapters.map(chapter => (
            <div key={chapter.id}>
              {/* Mobile Card View */}
              <div className="md:hidden p-4 border-t border-gray-200 hover:bg-gray-50">
                <div className="mb-3">
                  <div className="font-medium text-gray-900 mb-2 break-words">
                    {editingId === chapter.id ? (
                      <input
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        className="p-2 border rounded w-full text-sm"
                      />
                    ) : (
                      chapter.name
                    )}
                  </div>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs ${chapter.done ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                    {chapter.done ? "Completed" : "In Progress"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {editingId === chapter.id ? (
                    <>
                      <button onClick={saveEdit} className="text-green-600 hover:text-green-800 font-medium text-sm px-3 py-1.5 bg-green-50 rounded">Save</button>
                      <button onClick={cancelEdit} className="text-gray-600 hover:text-gray-800 font-medium text-sm px-3 py-1.5 bg-gray-50 rounded">Cancel</button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => toggleDone(chapter.id, chapter.done)}
                        className="text-teal-600 hover:text-teal-800 font-medium text-sm px-3 py-1.5 bg-teal-50 rounded"
                      >
                        {chapter.done ? "Incomplete" : "Complete"}
                      </button>
                      <button
                        onClick={() => startEdit(chapter.id, chapter.name)}
                        className="text-indigo-600 hover:text-indigo-800 font-medium text-sm px-3 py-1.5 bg-indigo-50 rounded"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(chapter.id)}
                        className="text-red-600 hover:text-red-800 font-medium text-sm px-3 py-1.5 bg-red-50 rounded"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Desktop Table View */}
              <div className="hidden md:grid grid-cols-12 gap-4 p-4 border-t border-gray-200 hover:bg-gray-50">
                <div className="col-span-6 font-medium text-gray-900 break-words">
                  {editingId === chapter.id ? (
                    <input
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      className="p-1 border rounded w-full"
                    />
                  ) : (
                    chapter.name
                  )}
                </div>
                <div className="col-span-2">
                  <span className={`px-2 py-1 rounded-full text-xs ${chapter.done ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                    {chapter.done ? "Completed" : "In Progress"}
                  </span>
                </div>
                <div className="col-span-4 flex gap-2 flex-wrap">
                  {editingId === chapter.id ? (
                    <>
                      <button onClick={saveEdit} className="text-green-600 hover:text-green-800 font-medium text-sm">Save</button>
                      <button onClick={cancelEdit} className="text-gray-600 hover:text-gray-800 font-medium text-sm">Cancel</button>
                    </>
                  ) : (
                    <>
                    <button
                      onClick={() => toggleDone(chapter.id, chapter.done)}
                      className="text-teal-600 hover:text-teal-800 font-medium text-sm"
                    >
                        {chapter.done ? "Mark Incomplete" : "Mark Complete"}
                      </button>
                      <button
                        onClick={() => startEdit(chapter.id, chapter.name)}
                        className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(chapter.id)}
                        className="text-red-600 hover:text-red-800 font-medium text-sm"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="mt-4 text-center sm:text-right text-gray-600 text-sm sm:text-base">
        Total chapters: {filteredChapters.length}
      </div>
    </div>
  );
};

export default ChaptersManagment;